import { transition, trigger, useAnimation } from '@angular/animations'
import { Component, computed, inject, OnInit } from '@angular/core'
import { NavigationExtras } from '@angular/router'
import {
  Experiment,
  getBucket,
  HardwareId,
  hwIdByProductKey,
  ProductKey,
  ProductVariant,
  RegistrationFlow,
  WebSignupQueryParam,
} from '@naturalcycles/shared'
import { DoYouOwnDeviceComponent } from '@src/app/components/modals/do-you-own-device/do-you-own-device.component.js'
import { Decorate } from '@src/app/core/decorators/decorators.js'
import { MixpanelService } from '@src/app/core/services/analytics/mixpanel.service.js'
import { CartService } from '@src/app/core/services/cart.service.js'
import { ExperimentService } from '@src/app/core/services/experiment.service.js'
import { ModalService } from '@src/app/core/services/modal.service.js'
import { NavService } from '@src/app/core/services/nav.service.js'
import { ProductService } from '@src/app/core/services/product.service.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { fadeAnimation, staggerAnimation } from '@src/app/core/util/animations.util.js'
import { flowUtils } from '@src/app/core/util/flow.util.js'
import { getRecommendedDevice } from '@src/app/core/util/quiz.util.js'
import { MixpanelEvent } from '@src/app/shared/typings/analytics.js'
import { ErrorHandlerType } from '@src/app/shared/typings/enum/error-handler.js'
import { Page } from '@src/app/shared/typings/enum/pages.js'
import { EnrichedProduct } from '@src/app/shared/typings/interfaces/products.js'
import { LoaderType } from '@src/app/shared/typings/ui.js'
import { FlowPage, FlowPageNavigator } from '../flow-page/flow.page.js'

@Component({
  selector: 'app-page-measuring-device',
  templateUrl: './measuring-device.page.component.html',
  styleUrls: ['./measuring-device.page.component.scss'],
  animations: [
    trigger('fadeAnimation', [transition('* => *', [useAnimation(fadeAnimation)])]),
    trigger('staggerAnimation', [transition('* => *', [useAnimation(staggerAnimation)])]),
  ],
  host: {
    '[class.ouraPage__abTest366]': 'isTest366()',
  },
  standalone: false,
})
export class MeasuringDevicePage implements FlowPage, OnInit {
  public navigator = new FlowPageNavigator(Page.measuringDevice)
  protected store = inject(SignalStore)
  private cartService = inject(CartService)
  private modalService = inject(ModalService)
  private mixpanelService = inject(MixpanelService)
  private productService = inject(ProductService)
  private experimentService = inject(ExperimentService)
  private navService = inject(NavService)

  protected Page = Page

  private thirdPartyDevices = [ProductKey.OURA_RING_DISCOUNT, ProductKey.APPLE_WATCH]
  protected measuringDevices = computed<EnrichedProduct[]>(() => this.getMeasuringDevices())

  protected isOuraFlow?: boolean
  protected cardSelected?: ProductKey
  protected bestDevice?: ProductKey
  protected hasLoadedDevices = false

  protected isTest366 = computed(
    () =>
      getBucket(
        this.store.$experiment().assignments[Experiment.QUIZ_OURA_FLOW_FIRST_SCREEN_ITERATION],
      ) === 'test' && this.isOuraFlow,
  )

  public async ngOnInit(): Promise<void> {
    const state = this.store.getState()
    void this.experimentService.logImpression(Experiment.INSURANCE_CAPTURE)
    void this.experimentService.logImpression(Experiment.T3_STOCK_DEPLETION)

    this.isOuraFlow = flowUtils.isOuraFlow({ state, params: this.navService.getQueryParams() })

    if (this.isOuraFlow) {
      await this.experimentService.logImpression(Experiment.QUIZ_OURA_FLOW_FIRST_SCREEN_ITERATION)
    }
  }

  private getMeasuringDevices(): EnrichedProduct[] {
    const quiz = this.store.$quiz()
    const product = this.store.$product()
    const defaultProduct = this.productService.getDefaultThermometer(product.items)
    // Early return after reading all of the signals, so we track dependencies correctly
    if (!this.store.$bootstrapComplete()) return []

    this.bestDevice = getRecommendedDevice(quiz.data, defaultProduct) || ''

    const devices = this.getEnrichedHardwares()

    const suggestedDeviceIndex = devices.findIndex(device => device.key === this.bestDevice)
    const suggestedDevice = devices[suggestedDeviceIndex]

    if (suggestedDevice) {
      devices.splice(suggestedDeviceIndex, 1)
      devices.unshift(suggestedDevice)
    }

    this.hasLoadedDevices = true
    return devices
  }

  private getEnrichedHardwares(): EnrichedProduct[] {
    return this.productService.enrichProducts(this.store.$product().items.filter(i => !!i.hwId))
  }

  protected async onDeviceSelected(productKey: ProductKey): Promise<void> {
    const cartDevice = this.cartService.getCartItems().find(i => i.key === productKey)
    if (this.thirdPartyDevices.includes(productKey)) {
      const ownsDevice = cartDevice?.variant === ProductVariant.ownsDevice

      if (ownsDevice) {
        // skip asking the same question again
        void this.setNavParamAndContinue(productKey, ownsDevice)
      } else {
        void this.modalService.show({
          component: DoYouOwnDeviceComponent,
          modalName: 'DoYouOwnDeviceModal',
          hideCloseButton: false,
          skipControls: true,
          sheetStyle: true,
          modalCentered: true,
          api: {
            measuringDevice: productKey,
            select: (ownsDevice: boolean) => {
              void this.modalService.remove()
              void this.setNavParamAndContinue(productKey, ownsDevice)
            },
          },
        })
      }
    } else {
      await this.setNavParamAndContinue(productKey)
    }
  }

  private async setNavParamAndContinue(
    selectedMeasuringDevice: ProductKey,
    ownsDevice?: boolean,
  ): Promise<void> {
    const hwId = this.mapMeasuringDeviceToHardwareId(selectedMeasuringDevice)
    const extras: NavigationExtras = {
      queryParams: {
        [WebSignupQueryParam.measuringDevice]: hwId,
      },
    }

    await this.cartService.selectDevice(hwId, selectedMeasuringDevice, ownsDevice)

    void this.mixpanelService.trackEvent(MixpanelEvent.DeviceSelected, {
      selectedMeasuringDevice,
      deviceFlow:
        Number(this.navService.getQueryParam(WebSignupQueryParam.flow || '')) || undefined,
      ownsDevice,
      availableDevices: this.measuringDevices().map(i => i.key),
    })

    await this.navigator.next(extras)
  }

  private mapMeasuringDeviceToHardwareId(selectedMeasuringDevice: ProductKey): HardwareId {
    return Number(hwIdByProductKey[selectedMeasuringDevice] || HardwareId.ORAL_THERMOMETER)
  }

  @Decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  async onOuraSelected(hasRing: boolean): Promise<void> {
    await this.cartService.selectDevice(HardwareId.OURA, ProductKey.OURA_RING_DISCOUNT, hasRing)

    void this.mixpanelService.trackEvent(MixpanelEvent.HasOuraRing, { hasRing })

    const extras: NavigationExtras = {
      queryParams: {
        [WebSignupQueryParam.flow]: RegistrationFlow.OURA.toString(),
      },
    }

    await this.navigator.next(extras)
  }
}
